/**
 * Product Table Component
 * Product-specific inventory table showing cases + units format, variant support,
 * dates (MFG + Expiry), documents, and expandable lot details.
 */

import React, { useState } from 'react';
import {
  Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Typography,
  Collapse, Chip, IconButton, Stack, alpha, useTheme, TextField, InputAdornment, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, Button, CircularProgress
} from '@mui/material';
import {
  ExpandMore, ExpandLess, Edit as EditIcon,
  Description as DescriptionIcon, Receipt as ReceiptIcon,
  AssignmentTurnedIn as AssignmentTurnedInIcon, Warning as WarningIcon,
  CheckCircle as CheckCircleIcon, Search as SearchIcon,
  Delete as DeleteIcon, Close as CloseIcon, Error as ErrorIcon
} from '@mui/icons-material';
import EditLotDialog from '../EditLotDialog';
import EditReorderDialog from '../EditReorderDialog';
import PaginationController from '../PaginationController';
import { supabase } from '../../../../../supabaseClient';

const LOT_BATCH_DEFAULT_ROWS = 12;

/**
 * Format total_base_units into "X cases + Y units" string
 */
function formatCasesUnits(totalBaseUnits, unitsPerCase, enableCases) {
  const total = Math.floor(totalBaseUnits || 0);
  if (!enableCases || !unitsPerCase || unitsPerCase <= 1) {
    return `${total.toLocaleString()} units`;
  }
  const cases = Math.floor(total / unitsPerCase);
  if (cases === 0) return `${total.toLocaleString()} units`;
  return `${cases.toLocaleString()} cases`;
}

function getStatusSeverity(item) {
  const qty = item.total_quantity || 0;
  if (qty === 0) return 'error';
  const rp = item.reorder_point || 0;
  if (rp > 0 && qty <= rp) return 'warning';
  return 'success';
}

export default function ProductTable({ data = [], config, inventoryType, expandable = false, onRefresh, noContainerBorder = false, canEdit = true, canDelete = true }) {
  const theme = useTheme();
  const [expandedRows, setExpandedRows] = useState({});
  const [lotBatchSearchTerms, setLotBatchSearchTerms] = useState({});
  const [lotBatchPagination, setLotBatchPagination] = useState({});
  const [editLotDialogOpen, setEditLotDialogOpen] = useState(false);
  const [editReorderDialogOpen, setEditReorderDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [deleteLotDialogOpen, setDeleteLotDialogOpen] = useState(false);
  const [deleteAggregateDialogOpen, setDeleteAggregateDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const handleRowClick = (key) => {
    if (!expandable) return;
    setExpandedRows(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // ── Delete single lot ──
  const confirmDeleteLot = async () => {
    if (!itemToDelete) return;
    try {
      setDeleting(true);

      const lotItem = itemToDelete;
      const productId = lotItem[config.itemIdField];
      const variantId = lotItem.variant_id || null;

      // Delete source transaction if exists
      if (lotItem.source_transaction_id) {
        await supabase.from(config.transactionsTable).delete().eq('id', lotItem.source_transaction_id);
      }

      // Log deletion transaction
      const deletedQty = Math.abs(lotItem.total_base_units || 0);
      const itemName = lotItem[config.tableName]?.name || lotItem.item_name || 'item';
      const lotParts = [
        lotItem.lot_number ? `Lot: ${lotItem.lot_number}` : null,
        lotItem.batch_number ? `Batch: ${lotItem.batch_number}` : null
      ].filter(Boolean).join(', ');

      await supabase.from(config.transactionsTable).insert({
        company_id: lotItem.company_id,
        [config.itemIdField]: productId,
        variant_id: variantId,
        transaction_type: 'inventory_deletion',
        quantity_change: -deletedQty,
        location_id: lotItem.location_id,
        lot_number: lotItem.lot_number,
        batch_number: lotItem.batch_number,
        created_at: new Date().toISOString(),
        notes: `Lot deleted — ${itemName}${lotParts ? ` (${lotParts})` : ''}`
      });

      // Delete the lot item
      const { error } = await supabase.from(config.itemsTable).delete().eq('id', lotItem.id);
      if (error) throw error;

      // Recalculate aggregate
      await supabase.rpc('recalculate_product_inventory_aggregate', {
        p_company_id: lotItem.company_id,
        p_product_id: productId,
        p_variant_id: variantId,
        p_location_id: lotItem.location_id
      });

      if (onRefresh) await onRefresh();
      setDeleteLotDialogOpen(false);
      setItemToDelete(null);
    } catch (err) {
      console.error('Error deleting lot:', err);
      alert('Failed to delete lot/batch: ' + err.message);
    } finally {
      setDeleting(false);
    }
  };

  // ── Delete all lots for aggregate ──
  const confirmDeleteAggregate = async () => {
    if (!itemToDelete) return;
    try {
      setDeleting(true);

      const actualItemId = itemToDelete.item_id || itemToDelete[config.itemIdField];
      const variantId = itemToDelete.variant_id || null;
      if (!actualItemId) throw new Error('Could not determine item ID');

      // Get all lot items for this specific variant
      let lotQuery = supabase
        .from(config.itemsTable)
        .select('id, source_transaction_id, total_base_units')
        .eq('company_id', itemToDelete.company_id)
        .eq(config.itemIdField, actualItemId)
        .eq('location_id', itemToDelete.location_id);
      if (variantId) {
        lotQuery = lotQuery.eq('variant_id', variantId);
      } else {
        lotQuery = lotQuery.is('variant_id', null);
      }
      const { data: allLotItems } = await lotQuery;

      // Delete source transactions
      const txIds = (allLotItems || []).map(i => i.source_transaction_id).filter(Boolean);
      if (txIds.length > 0) {
        await supabase.from(config.transactionsTable).delete().in('id', txIds);
      }

      const totalDeleted = (allLotItems || []).reduce((sum, l) => sum + (l.total_base_units || 0), 0);

      // Log deletion transaction
      await supabase.from(config.transactionsTable).insert({
        company_id: itemToDelete.company_id,
        [config.itemIdField]: actualItemId,
        variant_id: variantId,
        transaction_type: 'inventory_deletion',
        quantity_change: -totalDeleted,
        location_id: itemToDelete.location_id,
        created_at: new Date().toISOString(),
        notes: `All inventory deleted — ${itemToDelete.item_name || 'item'}`
      });

      // Delete all lot items by primary key ID
      const lotItemIds = (allLotItems || []).map(i => i.id).filter(Boolean);
      if (lotItemIds.length > 0) {
        for (const lotId of lotItemIds) {
          await supabase.from(config.itemsTable).delete().eq('id', lotId);
        }
      }
      // Also catch any remaining via compound key
      let deleteLotQuery = supabase.from(config.itemsTable).delete()
        .eq('company_id', itemToDelete.company_id)
        .eq(config.itemIdField, actualItemId)
        .eq('location_id', itemToDelete.location_id);
      if (variantId) { deleteLotQuery = deleteLotQuery.eq('variant_id', variantId); }
      else { deleteLotQuery = deleteLotQuery.is('variant_id', null); }
      await deleteLotQuery;

      // Fetch aggregate record ID, then delete by primary key
      let aggSelectQ = supabase.from(config.inventoryTable).select('id')
        .eq('company_id', itemToDelete.company_id)
        .eq(config.itemIdField, actualItemId)
        .eq('location_id', itemToDelete.location_id);
      if (variantId) { aggSelectQ = aggSelectQ.eq('variant_id', variantId); }
      else { aggSelectQ = aggSelectQ.is('variant_id', null); }
      const { data: aggRow } = await aggSelectQ.maybeSingle();
      if (aggRow) {
        await supabase.from(config.inventoryTable).delete().eq('id', aggRow.id);
      }

      if (onRefresh) await onRefresh();
      setDeleteAggregateDialogOpen(false);
      setItemToDelete(null);
    } catch (err) {
      console.error('Error deleting aggregate:', err);
      alert('Failed to delete inventory: ' + err.message);
    } finally {
      setDeleting(false);
    }
  };

  // ── Document viewer ──
  const viewDocument = async (bucket, filePath) => {
    if (!bucket || !filePath) return;
    try {
      const { data, error } = await supabase.storage.from(bucket).createSignedUrl(filePath, 3600);
      if (error || !data?.signedUrl) return;
      window.open(data.signedUrl, '_blank');
    } catch (err) {
      console.error('Error viewing document:', err);
    }
  };

  const documentTypeMap = {
    invoice:        { label: 'INV',  color: 'primary',   bucket: 'products-inventory-invoice-documents',  pathKey: 'invoice_file_path',        icon: <ReceiptIcon fontSize="small" /> },
    po:             { label: 'PO',   color: 'secondary', bucket: 'products-inventory-po',                 pathKey: 'po_file_path',             icon: <DescriptionIcon fontSize="small" />, sx: { bgcolor: '#00897b', color: 'white', '& .MuiChip-icon': { color: 'white' }, '&:hover': { bgcolor: '#00695c' } } },
    coa:            { label: 'COA',  color: 'success',   bucket: 'products-inventory-coa-documents',      pathKey: 'coa_file_path',            icon: <AssignmentTurnedInIcon fontSize="small" /> },
    sds:            { label: 'SDS',  color: 'warning',   bucket: 'products-inventory-sds-documents',      pathKey: 'sds_file_path',            icon: <DescriptionIcon fontSize="small" /> },
    bol:            { label: 'BOL',  color: 'info',      bucket: 'products-transactions-bol',             pathKey: 'bol_file_path',            icon: <DescriptionIcon fontSize="small" /> },
    production_doc: { label: 'PROD', color: undefined,   bucket: 'products-inventory-production-documents', pathKey: 'production_doc_file_path', icon: <DescriptionIcon fontSize="small" />, sx: { bgcolor: '#9c6fce', color: 'white', '& .MuiChip-icon': { color: 'white' }, '&:hover': { bgcolor: '#7b52a8' } } }
  };

  const renderDocuments = (lotItem) => {
    const trans = lotItem.source_transaction || {};
    // Also check direct lot-level document paths
    const available = Object.entries(documentTypeMap)
      .filter(([, doc]) => trans[doc.pathKey] || lotItem[doc.pathKey])
      .map(([key, doc]) => ({ ...doc, key, path: trans[doc.pathKey] || lotItem[doc.pathKey] }));

    if (available.length === 0) {
      return <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center' }}>No documents</Typography>;
    }
    return (
      <Stack direction="row" spacing={0.5} flexWrap="wrap" justifyContent="center" alignItems="center">
        {available.map(doc => (
          <Chip key={doc.key} label={doc.label} size="small" color={doc.sx ? undefined : doc.color} icon={doc.icon} clickable
            onClick={() => viewDocument(doc.bucket, doc.path)}
            sx={{ fontSize: '0.75rem', height: 24, ...(doc.sx || {}), '&:hover': { transform: 'scale(1.05)', ...(doc.sx?.['&:hover'] || {}) } }} />
        ))}
      </Stack>
    );
  };

  if (!data || data.length === 0) {
    return (
      <Paper sx={{ p: 8, textAlign: 'center', borderRadius: 3 }}>
        <Typography variant="h6" color="text.secondary">No product inventory found</Typography>
      </Paper>
    );
  }

  // Sort: alphabetically by product name, then by variant size highest→lowest
  const extractSize = (name) => {
    const m = name?.match(/(\d+(\.\d+)?)/);
    return m ? parseFloat(m[1]) : 0;
  };
  const sortedData = [...data].sort((a, b) => {
    const nameA = (a.item_name || '').toLowerCase();
    const nameB = (b.item_name || '').toLowerCase();
    if (nameA !== nameB) return nameA.localeCompare(nameB);
    const sizeA = extractSize(a.variant_name || a.item_name);
    const sizeB = extractSize(b.variant_name || b.item_name);
    return sizeB - sizeA; // highest first
  });

  const hSx = {
    fontWeight: 500, fontSize: '0.9rem', bgcolor: 'white',
    borderBottom: `2px solid ${theme.palette.primary.main}`,
    color: 'primary.main', py: 2, px: 3
  };

  return (
    <>
      <TableContainer
        component={noContainerBorder ? 'div' : Paper}
        sx={noContainerBorder ? { overflow: 'visible' } : {
          borderRadius: 3,
          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          boxShadow: `0 8px 24px ${alpha(theme.palette.grey[500], 0.15)}`,
          overflow: 'hidden'
        }}
      >
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...hSx, minWidth: 250 }}>Product</TableCell>
              <TableCell sx={hSx}>Location</TableCell>
              <TableCell sx={hSx} align="center">Cases</TableCell>
              <TableCell sx={hSx} align="center">Reorder Point</TableCell>
              <TableCell sx={hSx} align="center">Status</TableCell>
              <TableCell sx={{ ...hSx, width: 120 }} align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedData.map((item, index) => {
              const rowKey = `${item.item_id}-${item.variant_id || 'null'}-${item.location_id}`;
              const isExpanded = expandedRows[rowKey];
              const lotItems = item.lots || [];
              const totalBaseUnits = item.total_base_units || item.total_quantity || 0;
              const unitsPerCase = item.units_per_case || 1;
              const enableCases = item.enable_cases ?? true;
              const reorderPoint = item.reorder_point || 0;
              const status = getStatusSeverity(item);
              const hasInventory = totalBaseUnits > 0;

              return (
                <React.Fragment key={`${rowKey}-${index}`}>
                  <TableRow
                    hover
                    sx={{
                      cursor: 'default',
                      transition: 'all 0.2s ease',
                      bgcolor: hasInventory ? 'white' : alpha(theme.palette.error.main, 0.02),
                      '&:hover': {
                        bgcolor: 'grey.50',
                        transform: 'translateX(4px)',
                        transition: 'all 0.2s ease',
                        boxShadow: 'inset 4px 0 0 black'
                      },
                      '& .MuiTableCell-root': {
                        borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                        py: 3, px: 3
                      }
                    }}
                  >
                    {/* Product Name */}
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        {expandable && hasInventory && lotItems.length > 0 && (
                          <IconButton
                            size="small"
                            onClick={() => handleRowClick(rowKey)}
                            sx={{ color: 'primary.main', border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`, borderRadius: 1.5 }}
                          >
                            {isExpanded ? <ExpandLess /> : <ExpandMore />}
                          </IconButton>
                        )}
                        <Box>
                          <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '0.95rem', mb: 0.3 }}>
                            {item.item_name}
                          </Typography>
                          {item.variant_name && (
                            <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 500 }}>
                              {item.variant_name}
                            </Typography>
                          )}
                          {item.item_sku && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              SKU: {item.item_sku}
                            </Typography>
                          )}
                          {expandable && hasInventory && lotItems.length > 0 && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              {lotItems.length} lot{lotItems.length !== 1 ? 's' : ''}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>

                    {/* Location */}
                    <TableCell>
                      <Typography variant="body2" sx={{ fontSize: '1rem', color: 'text.primary' }}>
                        {item.location_name}
                      </Typography>
                    </TableCell>

                    {/* Cases */}
                    <TableCell align="center">
                      <Typography variant="h5" sx={{ fontWeight: 500, color: hasInventory ? 'text.primary' : 'text.disabled', fontSize: '1.1rem', mb: 0.5 }}>
                        {(() => {
                          if (lotItems.length > 0) {
                            // Sum cases from each lot individually (lots may have different unit configs)
                            const totalCases = lotItems.reduce((sum, lot) => {
                              const upc = lot.units_per_case || unitsPerCase;
                              const base = lot.total_base_units || 0;
                              return sum + Math.floor(base / upc);
                            }, 0);
                            return `${totalCases.toLocaleString()} cases`;
                          }
                          return formatCasesUnits(totalBaseUnits, unitsPerCase, enableCases);
                        })()}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
                        {totalBaseUnits.toLocaleString()} total units
                      </Typography>
                    </TableCell>

                    {/* Reorder Point */}
                    <TableCell align="center">
                      <Typography variant="h6" sx={{ fontWeight: 400, color: 'primary.main', fontSize: '1.05rem' }}>
                        {reorderPoint > 0
                          ? `${reorderPoint.toLocaleString()} units`
                          : 'Not set'}
                      </Typography>
                    </TableCell>

                    {/* Status */}
                    <TableCell align="center">
                      {status === 'error'   && <Chip label="Out of Stock" size="small" color="error" icon={<ErrorIcon />} />}
                      {status === 'warning' && <Chip label="Low Stock" size="small" color="warning" icon={<WarningIcon />} />}
                      {status === 'success' && <Chip label="In Stock" size="small" color="success" icon={<CheckCircleIcon />} />}
                    </TableCell>

                    {/* Actions */}
                    <TableCell align="center">
                      <IconButton size="small" color="primary" disabled={!canEdit} onClick={() => { setSelectedItem(item); setEditReorderDialogOpen(true); }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" disabled={!canDelete} onClick={() => { setItemToDelete(item); setDeleteAggregateDialogOpen(true); }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>

                  {/* Expandable Lot Rows */}
                  {expandable && (
                    <TableRow>
                      <TableCell sx={{ py: 0, px: 0, border: 'none' }} colSpan={6}>
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <Box sx={{
                            p: 4, bgcolor: 'white',
                            borderTop: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
                            borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`
                          }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 3 }}>
                              <Typography variant="h6" sx={{ fontWeight: 500, color: 'primary.main', fontSize: '1.1rem' }}>
                                Lot/Batch Details ({lotItems.length} lot{lotItems.length !== 1 ? 's' : ''})
                              </Typography>
                              <TextField
                                placeholder="Search lot/batch..."
                                size="small"
                                value={lotBatchSearchTerms[rowKey] || ''}
                                onChange={(e) => setLotBatchSearchTerms(prev => ({ ...prev, [rowKey]: e.target.value }))}
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      <SearchIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                                    </InputAdornment>
                                  )
                                }}
                                sx={{
                                  width: { xs: '100%', sm: '400px' },
                                  '& .MuiOutlinedInput-root': { borderRadius: 2, backgroundColor: 'white' }
                                }}
                              />
                            </Box>

                            {(() => {
                              const searchTerm = (lotBatchSearchTerms[rowKey] || '').toLowerCase();
                              const lbPage = lotBatchPagination[rowKey]?.page ?? 0;
                              const lbRowsPerPage = lotBatchPagination[rowKey]?.rowsPerPage ?? LOT_BATCH_DEFAULT_ROWS;
                              const filtered = lotItems.filter(lot => {
                                if (!searchTerm) return true;
                                return (lot.lot_number || '').toLowerCase().includes(searchTerm)
                                  || (lot.batch_number || '').toLowerCase().includes(searchTerm);
                              });
                              const sorted = [...filtered].sort((a, b) => {
                                const da = a.manufactured_date ? new Date(a.manufactured_date) : new Date(0);
                                const db = b.manufactured_date ? new Date(b.manufactured_date) : new Date(0);
                                return db - da;
                              });
                              const paginated = sorted.slice(lbPage * lbRowsPerPage, (lbPage + 1) * lbRowsPerPage);

                              return (
                                <>
                                  <TableContainer component={Paper} sx={{
                                    borderRadius: 2,
                                    border: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
                                    overflow: 'hidden'
                                  }}>
                                    <Table>
                                      <TableHead>
                                        <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.08) }}>
                                          {(() => {
                                            const lhSx = { fontWeight: 500, color: 'primary.main', fontSize: '1rem', py: 2.5, px: 3 };
                                            return (
                                              <>
                                                <TableCell sx={lhSx}>Quantity</TableCell>
                                                <TableCell sx={lhSx}>Cases</TableCell>
                                                <TableCell sx={lhSx}>Lot/Batch Details</TableCell>
                                                <TableCell sx={lhSx}>Dates</TableCell>
                                                <TableCell sx={lhSx} align="center">Status</TableCell>
                                                <TableCell sx={lhSx} align="center">Documents</TableCell>
                                                <TableCell sx={lhSx} align="center">Actions</TableCell>
                                              </>
                                            );
                                          })()}
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {paginated.map((lotItem, lotIdx) => {
                                          const lotTotalBase = lotItem.total_base_units || 0;
                                          const lotUnitsPerCase = lotItem.units_per_case || unitsPerCase;
                                          const lotEnableCases = lotItem.enable_cases ?? enableCases;

                                          // Expiry logic
                                          const parseDateLocal = (ds) => {
                                            if (!ds) return null;
                                            const [y, m, d] = ds.split('-').map(Number);
                                            if (isNaN(y) || isNaN(m) || isNaN(d)) return null;
                                            return new Date(y, m - 1, d);
                                          };
                                          const formatDateOnly = (ds) => {
                                            const dt = parseDateLocal(ds);
                                            return dt ? dt.toLocaleDateString() : '—';
                                          };
                                          const today = new Date(); today.setHours(0, 0, 0, 0);
                                          const in30Days = new Date(today); in30Days.setDate(today.getDate() + 30);
                                          const expDateObj = parseDateLocal(lotItem.expiry_date);
                                          const isExpired = expDateObj && expDateObj <= today;
                                          const isExpiringSoon = expDateObj && !isExpired && expDateObj < in30Days;

                                          return (
                                            <TableRow
                                              key={`lot-${lotItem.id}-${lotIdx}`}
                                              hover
                                              sx={{
                                                transition: 'all 0.2s ease',
                                                '&:hover': {
                                                  bgcolor: 'grey.50',
                                                  transform: 'translateX(4px)',
                                                  transition: 'all 0.2s ease',
                                                  boxShadow: 'inset 4px 0 0 black'
                                                },
                                                '&:nth-of-type(even)': { bgcolor: alpha(theme.palette.grey[50], 0.3) }
                                              }}
                                            >
                                              {/* Quantity */}
                                              <TableCell sx={{ py: 3, px: 3 }}>
                                                <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '1rem', mb: 0.5 }}>
                                                  {lotTotalBase.toLocaleString()} units
                                                </Typography>
                                                {(lotItem.enable_pallets ?? false) && (lotItem.pallet_quantity || 0) > 0 && (
                                                  <Box sx={{ mb: 0.5 }}>
                                                    <Chip label={`${(lotItem.pallet_quantity || 0).toLocaleString()} ${lotItem.pallet_quantity === 1 ? 'Pallet' : 'Pallets'}`} size="medium" sx={{ fontSize: '0.8rem', height: 24, fontWeight: 400 }} />
                                                  </Box>
                                                )}
                                                <Tooltip title={`This lot uses: ${lotUnitsPerCase} units/case${(lotItem.enable_pallets ?? false) ? `, ${lotItem.cases_per_pallet || 1} cases/pallet` : ''}`}>
                                                  <Chip
                                                    label={`Config: ${lotUnitsPerCase}/${lotItem.cases_per_pallet || 1}`}
                                                    size="small"
                                                    variant="outlined"
                                                    color="info"
                                                    sx={{ fontSize: '0.7rem', height: 20, fontWeight: 400, mt: 0.5 }}
                                                  />
                                                </Tooltip>
                                              </TableCell>

                                              {/* Cases */}
                                              <TableCell sx={{ py: 3, px: 3 }}>
                                                {(lotEnableCases || lotUnitsPerCase > 1) ? (
                                                  <>
                                                    <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '0.95rem' }}>
                                                      {Math.floor(lotTotalBase / lotUnitsPerCase).toLocaleString()} cases
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                      {lotTotalBase.toLocaleString()} total units
                                                    </Typography>
                                                  </>
                                                ) : (
                                                  <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.9rem' }}>
                                                    NA
                                                  </Typography>
                                                )}
                                              </TableCell>

                                              {/* Lot/Batch Details */}
                                              <TableCell sx={{ py: 3, px: 3 }}>
                                                {lotItem.lot_number && (
                                                  <Typography variant="body1" sx={{ fontSize: '0.9rem', mb: 0.5 }}>Lot: {lotItem.lot_number}</Typography>
                                                )}
                                                {lotItem.batch_number && (
                                                  <Typography variant="body1" sx={{ fontSize: '0.9rem', mb: 0.5 }}>Batch: {lotItem.batch_number}</Typography>
                                                )}
                                                {!lotItem.lot_number && !lotItem.batch_number && (
                                                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.9rem' }}>No lot/batch info</Typography>
                                                )}
                                              </TableCell>

                                              {/* Dates */}
                                              <TableCell sx={{ py: 3, px: 3 }}>
                                                {lotItem.manufactured_date && (
                                                  <Typography variant="body1" sx={{ fontWeight: 400, display: 'block', fontSize: '0.9rem', mb: 0.5 }}>
                                                    Mfg: {formatDateOnly(lotItem.manufactured_date)}
                                                  </Typography>
                                                )}
                                                {lotItem.expiry_date && (
                                                  <Typography
                                                    variant="body1"
                                                    sx={{
                                                      fontWeight: isExpired || isExpiringSoon ? 500 : 400,
                                                      display: 'block',
                                                      fontSize: '0.9rem',
                                                      color: isExpired ? 'error.main' : isExpiringSoon ? 'warning.main' : 'text.primary'
                                                    }}
                                                  >
                                                    Exp: {formatDateOnly(lotItem.expiry_date)}
                                                    {isExpired && ' (EXPIRED)'}
                                                    {isExpiringSoon && ' (Soon)'}
                                                  </Typography>
                                                )}
                                                {lotItem.shelf_life_months != null && lotItem.shelf_life_months > 0 && (
                                                  <Typography variant="body2" sx={{ color: 'text.secondary', display: 'block', fontSize: '0.85rem', mt: 0.5 }}>
                                                    Shelf Life: {lotItem.shelf_life_months} month{lotItem.shelf_life_months !== 1 ? 's' : ''}
                                                  </Typography>
                                                )}
                                                {!lotItem.manufactured_date && !lotItem.expiry_date && (
                                                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.9rem' }}>No date info</Typography>
                                                )}
                                              </TableCell>

                                              {/* Status */}
                                              <TableCell sx={{ py: 3, px: 3 }} align="center">
                                                <Stack spacing={0.5} alignItems="center">
                                                  {isExpired ? (
                                                    <Chip label="Expired" size="small" color="error" icon={<ErrorIcon />} />
                                                  ) : isExpiringSoon ? (
                                                    <Chip label="Expiring Soon" size="small" color="warning" icon={<WarningIcon />} />
                                                  ) : (
                                                    <Chip
                                                      label={lotItem.is_active ? 'Active' : 'Inactive'}
                                                      size="small"
                                                      color={lotItem.is_active ? 'success' : 'default'}
                                                      icon={lotItem.is_active ? <CheckCircleIcon /> : undefined}
                                                    />
                                                  )}
                                                </Stack>
                                              </TableCell>

                                              {/* Documents */}
                                              <TableCell sx={{ py: 3, px: 3 }} align="center">
                                                {renderDocuments(lotItem)}
                                              </TableCell>

                                              {/* Actions */}
                                              <TableCell sx={{ py: 3, px: 3 }} align="center">
                                                <IconButton size="small" color="primary" disabled={!canEdit} onClick={() => {
                                                  setSelectedItem({
                                                    ...lotItem,
                                                    _isProduct: true,
                                                    _unitsPerCase: lotUnitsPerCase,
                                                    _enableCases: lotEnableCases
                                                  });
                                                  setEditLotDialogOpen(true);
                                                }}>
                                                  <EditIcon fontSize="small" />
                                                </IconButton>
                                                <IconButton size="small" color="error" disabled={!canDelete} onClick={() => { setItemToDelete(lotItem); setDeleteLotDialogOpen(true); }}>
                                                  <DeleteIcon fontSize="small" />
                                                </IconButton>
                                              </TableCell>
                                            </TableRow>
                                          );
                                        })}
                                        {paginated.length === 0 && (
                                          <TableRow>
                                            <TableCell colSpan={7} sx={{ textAlign: 'center', py: 4 }}>
                                              <Typography color="text.secondary">No lots found</Typography>
                                            </TableCell>
                                          </TableRow>
                                        )}
                                      </TableBody>
                                    </Table>
                                  </TableContainer>
                                  {filtered.length > LOT_BATCH_DEFAULT_ROWS && (
                                    <PaginationController
                                      count={filtered.length}
                                      page={lbPage}
                                      onPageChange={(_, newPage) => setLotBatchPagination(prev => ({
                                        ...prev,
                                        [rowKey]: { page: newPage, rowsPerPage: prev[rowKey]?.rowsPerPage ?? LOT_BATCH_DEFAULT_ROWS }
                                      }))}
                                      rowsPerPage={lbRowsPerPage}
                                      onRowsPerPageChange={(val) => setLotBatchPagination(prev => ({
                                        ...prev,
                                        [rowKey]: { page: 0, rowsPerPage: val }
                                      }))}
                                      sx={{ py: 1.5, px: 2 }}
                                    />
                                  )}
                                </>
                              );
                            })()}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {selectedItem && (
        <>
          <EditLotDialog
            open={editLotDialogOpen}
            onClose={() => { setEditLotDialogOpen(false); setSelectedItem(null); }}
            item={selectedItem} config={config} inventoryType={inventoryType} onSuccess={onRefresh} canDelete={canDelete}
          />
          <EditReorderDialog
            open={editReorderDialogOpen}
            onClose={() => { setEditReorderDialogOpen(false); setSelectedItem(null); }}
            item={selectedItem} config={config} inventoryType={inventoryType} onSuccess={onRefresh}
          />
        </>
      )}

      {/* Delete Lot Dialog */}
      <Dialog open={deleteLotDialogOpen} onClose={() => !deleting && setDeleteLotDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, pb: 2 }}>
          <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: alpha(theme.palette.error.main, 0.1), display: 'flex', alignItems: 'center' }}>
            <DeleteIcon sx={{ color: 'error.main', fontSize: 28 }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 500, mb: 0.5 }}>Delete Lot/Batch</Typography>
            <Typography variant="body2" color="text.secondary">This will remove this lot/batch and update inventory totals.</Typography>
          </Box>
          <IconButton onClick={() => setDeleteLotDialogOpen(false)} disabled={deleting} sx={{ mt: -1, mr: -1 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {itemToDelete && (
            <Paper elevation={0} sx={{ p: 2.5, bgcolor: alpha(theme.palette.error.main, 0.04), border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`, borderRadius: 2 }}>
              <Typography variant="subtitle2" color="error.main" sx={{ fontWeight: 600, mb: 1 }}>
                {itemToDelete.item_name || itemToDelete[config.tableName]?.name || 'Product'}
              </Typography>
              <Typography variant="body2"><strong>Lot:</strong> {itemToDelete.lot_number || '—'} / Batch: {itemToDelete.batch_number || '—'}</Typography>
              <Typography variant="body2">
                <strong>Quantity:</strong> {formatCasesUnits(itemToDelete.total_base_units || 0, itemToDelete.units_per_case || 1, itemToDelete.enable_cases ?? true)}
              </Typography>
            </Paper>
          )}
          <Paper elevation={0} sx={{ mt: 2, p: 2, bgcolor: alpha(theme.palette.warning.main, 0.08), border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`, borderRadius: 2, display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
            <WarningIcon sx={{ color: 'error.main', mt: 0.2 }} />
            <Typography variant="body2" color="text.secondary">This action cannot be undone.</Typography>
          </Paper>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button onClick={() => setDeleteLotDialogOpen(false)} disabled={deleting} sx={{ borderRadius: 2, px: 3 }}>Cancel</Button>
          <Button variant="contained" color="error" onClick={confirmDeleteLot} disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : <DeleteIcon />}
            sx={{ borderRadius: 2, px: 3 }}>
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Aggregate Dialog */}
      <Dialog open={deleteAggregateDialogOpen} onClose={() => !deleting && setDeleteAggregateDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, pb: 2 }}>
          <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: alpha(theme.palette.error.main, 0.1), display: 'flex', alignItems: 'center' }}>
            <DeleteIcon sx={{ color: 'error.main', fontSize: 28 }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 500, mb: 0.5 }}>Delete All Inventory</Typography>
            <Typography variant="body2" color="text.secondary">This will remove ALL lots/batches and inventory records for this product at this location.</Typography>
          </Box>
          <IconButton onClick={() => setDeleteAggregateDialogOpen(false)} disabled={deleting} sx={{ mt: -1, mr: -1 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {itemToDelete && (
            <Paper elevation={0} sx={{ p: 2.5, bgcolor: alpha(theme.palette.error.main, 0.04), border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`, borderRadius: 2 }}>
              <Typography variant="subtitle2" color="error.main" sx={{ fontWeight: 600, mb: 1 }}>{itemToDelete.item_name}</Typography>
              <Typography variant="body2"><strong>Location:</strong> {itemToDelete.location_name}</Typography>
              <Typography variant="body2">
                <strong>Total Stock:</strong> {formatCasesUnits(itemToDelete.total_base_units || itemToDelete.total_quantity || 0, itemToDelete.units_per_case || 1, itemToDelete.enable_cases ?? true)}
              </Typography>
            </Paper>
          )}
          <Paper elevation={0} sx={{ mt: 2, p: 2, bgcolor: alpha(theme.palette.warning.main, 0.08), border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`, borderRadius: 2, display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
            <WarningIcon sx={{ color: 'error.main', mt: 0.2 }} />
            <Typography variant="body2" color="text.secondary">This action cannot be undone.</Typography>
          </Paper>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button onClick={() => setDeleteAggregateDialogOpen(false)} disabled={deleting} sx={{ borderRadius: 2, px: 3 }}>Cancel</Button>
          <Button variant="contained" color="error" onClick={confirmDeleteAggregate} disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : <DeleteIcon />}
            sx={{ borderRadius: 2, px: 3 }}>
            {deleting ? 'Deleting...' : 'Delete All'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
